import { StateError } from '../errors';
import { rngInt, type RngHolder } from '../math/rng';
import { scaledCounts, type GameConfigShape } from '../config/gameConfig';
import { carriesDisc, type GameDef } from '../engine/gameDef';
import { validateMapDef, type HarborDir, type MapDefJson, type MapValidationEnv } from './mapSchema';
import { HEX_DIRS, hexNeighbors, hexagonCells, parseEdgeId, ringCells, type HexCoord } from './axial';
import { harborEdgeOf } from './graph';
import { spiralWalk, assignSpiralDiscs } from './spiral';
import type { ResolvedMap } from './graph';
import { resolveSeaMap } from './regionalResolve';

// Map resolution (plan §9.2 draw order — NORMATIVE; a changed draw is a golden-log break):
//   fixed maps:      no draws.
//   classic-random:  R1 harbor-kind FY → R2 terrain FY → R3 spiral corner rngInt(6)
//                    → (R5) red-spacing rejection repeats R2–R3 (harbors stand).
//   scaled-N:        ring fill + harbor positions deterministic, then terrain FY →
//                    disc FY → harbor-kind FY → (G6) rejection repeats terrain+disc FYs.
// All draws come from the ONE injected RngHolder (GameState.rng at init — plan §9.1).

/** Fisher–Yates from the end (i = n−1..1, j = rngInt(i+1)) — the normative shuffle. */
export function shuffleInPlace<T>(arr: T[], rng: RngHolder): void {
  for (let i = arr.length - 1; i >= 1; i--) {
    const j = rngInt(rng, i + 1);
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
}

/** True iff two red-numbered discs sit on adjacent hexes (the R5/G6 rejection predicate). */
export function hasRedAdjacency(hexes: Array<{ q: number; r: number; disc: number | null }>, red: readonly number[]): boolean {
  const redSet = new Set(red);
  const redCells = hexes.filter((h) => h.disc !== null && redSet.has(h.disc));
  const redKeys = new Set(redCells.map((h) => `${h.q},${h.r}`));
  for (const c of redCells) {
    for (const n of hexNeighbors(c)) if (redKeys.has(`${n.q},${n.r}`)) return true;
  }
  return false;
}

/**
 * Expand a count table into a flat multiset list in CANONICAL KEY ORDER (D25).
 * Declaration order was the H4 soak's replay-divergence bug: a def/config Record
 * round-tripped through canonical JSON comes back key-sorted, so any RNG draw fed by
 * insertion order resolved a DIFFERENT board on replay (scaled maps only — fixed maps
 * never draw tiles, which is why every earlier suite missed it). §9 determinism
 * demands total ordering independent of object key insertion: sorted, always.
 */
function expandPool<T extends string | number>(counts: Readonly<Record<string, number>>, key: (k: string) => T): T[] {
  const out: T[] = [];
  const keys = Object.keys(counts).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)); // canonicalJson's collation
  for (const k of keys) for (let i = 0; i < (counts[k] ?? 0); i++) out.push(key(k));
  return out;
}

/**
 * The §6.7 validation env resolveMap itself uses — exported so paste-time checks can't drift
 * (T-NET-019). H20-S1: a def ACCEPTS a sea block iff it composes the sea modules — the Open
 * Sea package's module ids all begin `sea-` — because only such a def can play one; the paste
 * path validates against the classic def and so refuses a pasted sea map here.
 */
export function validationEnv(def: GameDef, cfg: GameConfigShape): MapValidationEnv {
  return {
    tiles: new Map(def.tiles.map((t) => [t.id, t.numbered === true ? { produces: t.produces, numbered: true } : { produces: t.produces }])),
    resourceIds: new Set(def.resources.map((r) => r.id)),
    pieceIds: new Set(def.pieces.map((p) => p.id)),
    maxHexes: cfg.engine.maxHexes,
    acceptsSea: def.modules.some((m) => m.startsWith('sea-')),
  };
}

/** Resolve to concrete hexes/harbors, drawing per the normative order (plan §9.2). */
export function resolveMap(def: GameDef, cfg: GameConfigShape, rng: RngHolder, lookupFixed: (mapId: string) => MapDefJson): ResolvedMap {
  const env = validationEnv(def, cfg);
  const spec = def.map;

  if (spec.kind === 'generated') {
    return resolveScaled(def, cfg, rng, spec.seats);
  }

  const json = validateMapDef(spec.kind === 'fixed' ? lookupFixed(spec.mapId) : spec.json, env);
  const tileOf = new Map(def.tiles.map((t) => [t.id, t]));

  const base: ResolvedMap = {
    name: json.name,
    minSeats: json.minSeats,
    maxSeats: json.maxSeats,
    hexes: [],
    harbors: json.harbors.map((h) => ({ ...h })),
    fixedPieces: (json.fixedPieces ?? []).map((p) => ({
      owner: p.owner,
      piece: p.piece,
      ...(p.vertex ? { vertex: { ...p.vertex } } : {}),
      ...(p.edge ? { edge: { ...p.edge } } : {}),
    })),
    overrides: json.overrides
      ? {
          ...(json.overrides.supplyStock !== undefined ? { supplyStock: json.overrides.supplyStock } : {}),
          ...(json.overrides.discardOver !== undefined ? { discardOver: json.overrides.discardOver } : {}),
          ...(json.overrides.victoryTarget !== undefined ? { victoryTarget: json.overrides.victoryTarget } : {}),
          ...(json.overrides.piecePools ? { piecePools: { ...json.overrides.piecePools } } : {}),
        }
      : {},
  };

  if (json.custom !== undefined) {
    const c = json.custom;
    base.custom = {
      base: c.base,
      noStart: c.noStart.map((x) => ({ q: x.q, r: x.r })),
      bonus: c.bonus === null ? null : { vp: c.bonus.vp, regions: c.bonus.regions.map((reg) => reg.map((x) => ({ q: x.q, r: x.r }))) },
      ...(c.portPool !== undefined ? { portPool: [...c.portPool] } : {}),
    };
  }
  // H20-S1: a sea map deals by region (W1–W4) and never touches the spiral; a classic map
  // — including one whose JSON says discPolicy 'random' — resolves exactly as it always has
  if (json.sea !== undefined) return resolveSeaMap(def, cfg, rng, json, base);

  const hasRandomTiles = json.hexes.some((h) => h.tile === 'random');
  const hasAutoDiscs = json.hexes.some((h) => h.disc === 'auto');
  if (!hasRandomTiles && !hasAutoDiscs) {
    // fully fixed: no draws at all (T-MAP-002 asserts the rng is untouched)
    base.hexes = json.hexes.map((h) => ({ q: h.q, r: h.r, tile: h.tile, disc: h.disc === 'auto' ? null : h.disc }));
    return base;
  }

  // ── classic-random path (plan §9.2 R-steps) ────────────────────────────────
  // R1: harbor KINDS shuffled over the fixed positions (once — the arrangement stands
  // through red-spacing rejection, which re-rolls only terrain + corner).
  const kinds = base.harbors.map((h) => h.kind);
  shuffleInPlace(kinds, rng);
  base.harbors = base.harbors.map((h, i) => ({ ...h, kind: kinds[i]! }));

  const cells = json.hexes.map(({ q, r }) => ({ q, r }));
  const cellKey = (c: HexCoord): string => `${c.q},${c.r}`;
  // cells dealt in tuple order (r, then q) — plan §9.2 R2
  const dealOrder = [...cells].sort((a, b) => a.r - b.r || a.q - b.q);
  const tilePool = expandPool(json.randomPools?.tiles ?? {}, (k) => k);
  const bound = cfg.engine.mapGenRejectBound;

  // Review fix: seed the FIXED tiles, and deal the random pool ONLY over the cells that
  // asked for 'random'. The old code dealt the pool across dealOrder (EVERY cell), so a
  // single tile:'random' hex on an otherwise-fixed pasted map overwrote all 18 fixed tiles.
  // For a fully-random map (classic-random) fixedTile is empty and randomCells === dealOrder,
  // so the pool size and RNG draw count are unchanged — no golden/hash drift.
  const fixedTile = new Map<string, string>();
  for (const h of json.hexes) if (h.tile !== 'random') fixedTile.set(cellKey({ q: h.q, r: h.r }), h.tile);
  const randomCells = dealOrder.filter((c) => !fixedTile.has(cellKey(c)));

  for (let attempt = 0; attempt < bound; attempt++) {
    // R2: terrain deal — FY over the pool in its declaration order, assigned in deal order
    const tiles = [...tilePool];
    shuffleInPlace(tiles, rng);
    const tileAt = new Map<string, string>(fixedTile);
    randomCells.forEach((c, i) => tileAt.set(cellKey(c), tiles[i]!));

    // R3: spiral corner draw over the ring corners, ccw from east
    const radius = Math.max(...cells.map((c) => Math.max(Math.abs(c.q), Math.abs(c.r), Math.abs(c.q + c.r))));
    const corner = rngInt(rng, 6);
    const walk = spiralWalk(radius, corner);
    if (walk.length !== cells.length) {
      throw new StateError(`spiral disc policy needs a full hexagon (walk ${walk.length} vs ${cells.length} cells)`);
    }
    const skipTiles = new Set(def.tiles.filter((t) => !carriesDisc(t)).map((t) => t.id));
    const discs = assignSpiralDiscs(walk, (h) => tileAt.get(cellKey(h))!, skipTiles, cfg.engine.spiralLetters);

    const hexes = cells.map((c) => ({
      q: c.q,
      r: c.r,
      tile: tileAt.get(cellKey(c))!,
      disc: discs.get(`h${c.q},${c.r}`) ?? null,
    }));

    // R5: red-spacing rejection — repeat R2–R3 with fresh draws (harbors stand)
    if (json.redSpacing && hasRedAdjacency(hexes, cfg.engine.redNumbers)) continue;
    // a producing tile must never end up disc-less through the spiral (structural guard)
    for (const h of hexes) {
      const t = tileOf.get(h.tile);
      if (t !== undefined && carriesDisc(t) && h.disc === null) throw new StateError(`spiral left producing hex h${h.q},${h.r} disc-less`);
    }
    base.hexes = hexes;
    return base;
  }
  throw new StateError(`map generation exhausted the rejection bound (${bound}) — layout constraints unsatisfiable`);
}

/** scaled-N (plan §6.6 geometry + §9.2 G-steps). */
function resolveScaled(def: GameDef, cfg: GameConfigShape, rng: RngHolder, seats: number): ResolvedMap {
  const resourceOrder = def.resources.map((r) => r.id);
  const counts = scaledCounts(cfg, resourceOrder, seats);

  // deterministic geometry (plan §6.6 as amended by D20): the largest full hexagon that
  // fits, then the NEXT ring's cells interleaved (even indices first, then odd) from
  // corner E — spreads the bulge around the coast; no draws. b=1 → radius 2 + 11 of ring
  // 3; b=2 → radius 3 + 4 of ring 4.
  let radius = 0;
  while (hexagonCells(radius + 1).length <= counts.landHexes) radius += 1;
  const cells: HexCoord[] = [...hexagonCells(radius)];
  if (counts.landHexes > cells.length) {
    const nextRing = ringCells(radius + 1, 0);
    const order = [...nextRing.filter((_, i) => i % 2 === 0), ...nextRing.filter((_, i) => i % 2 === 1)];
    for (const c of order) {
      if (cells.length >= counts.landHexes) break;
      cells.push(c);
    }
  }
  if (cells.length !== counts.landHexes) {
    throw new StateError(`scaled-${seats}: geometry yields ${cells.length} cells for ${counts.landHexes} land hexes`);
  }
  const cellKey = (c: HexCoord): string => `${c.q},${c.r}`;
  const cellSet = new Set(cells.map(cellKey));

  // harbor positions (plan §6.6 as amended by D21): coastal edges ordered as a RING by the
  // angle of their midpoint around the board center (generated boards are star-shaped, so
  // this is a true cycle), H picks evenly spaced by index, and a pick that would SHARE a
  // vertex with the previous pick advances to the next edge — harbors are vertex-disjoint
  // by construction, as on the physical frame. Deterministic; no draws.
  const dirNames: HarborDir[] = ['E', 'NE', 'NW', 'W', 'SW', 'SE']; // HEX_DIRS order
  const sorted = [...cells].sort((a, b) => a.r - b.r || a.q - b.q);
  const coastRaw: Array<{ q: number; r: number; dir: HarborDir }> = [];
  for (const c of sorted) {
    hexNeighbors(c).forEach((n, i) => {
      if (!cellSet.has(cellKey(n))) coastRaw.push({ q: c.q, r: c.r, dir: dirNames[i]! });
    });
  }
  const midpointAngle = (e: { q: number; r: number; dir: HarborDir }): number => {
    // edge midpoint ≈ hex center pushed half-way toward the faced neighbor, in world coords
    const di = dirNames.indexOf(e.dir);
    const d = HEX_DIRS[di]!;
    const cq = e.q + d.q / 2;
    const cr = e.r + d.r / 2;
    const x = Math.sqrt(3) * (cq + cr / 2);
    const y = -1.5 * cr; // +y up (plan §3.2 world mapping)
    return Math.atan2(y, x);
  };
  const coast = [...coastRaw].sort((a, b) => midpointAngle(a) - midpointAngle(b));
  const H = counts.harborsTotal;
  if (coast.length < 2 * H) throw new StateError(`scaled-${seats}: only ${coast.length} coastal edges for ${H} vertex-disjoint harbors`);
  const positions: Array<{ q: number; r: number; dir: HarborDir }> = [];
  const usedVertices = new Set<string>();
  let cursor = 0;
  for (let i = 0; i < H; i++) {
    const target = Math.floor((i * coast.length) / H);
    cursor = Math.max(cursor, target);
    for (let hop = 0; hop < coast.length; hop++) {
      const cand = coast[(cursor + hop) % coast.length]!;
      const [va, vb] = parseEdgeId(harborEdgeOf(cand.q, cand.r, cand.dir));
      if (!usedVertices.has(va) && !usedVertices.has(vb)) {
        positions.push(cand);
        usedVertices.add(va);
        usedVertices.add(vb);
        cursor = cursor + hop + 1;
        break;
      }
    }
  }
  if (positions.length !== H) throw new StateError(`scaled-${seats}: could not place ${H} vertex-disjoint harbors`);

  const tilePool: Record<string, number> = { ...counts.terrain };
  const discPoolCounts: Record<string, number> = {};
  for (const [num, n] of Object.entries(counts.discs)) discPoolCounts[num] = n;
  const bound = cfg.engine.mapGenRejectBound;

  // G-steps: terrain FY → disc FY → (kinds after acceptance? NO — kinds shuffle once,
  // after terrain+discs settle, per plan §9.2 G-order: terrain, discs, kinds; rejection
  // repeats terrain+discs only, so kinds draw AFTER the accepted layout).
  for (let attempt = 0; attempt < bound; attempt++) {
    const tiles = expandPool(tilePool, (k) => k);
    shuffleInPlace(tiles, rng);
    const tileAt = new Map<string, string>();
    sorted.forEach((c, i) => tileAt.set(cellKey(c), tiles[i]!));

    const producing = sorted.filter((c) => def.tiles.find((t) => t.id === tileAt.get(cellKey(c)))!.produces !== null);
    const discPool = expandPool(discPoolCounts, (k) => Number(k));
    if (discPool.length !== producing.length) {
      throw new StateError(`scaled-${seats}: ${discPool.length} discs for ${producing.length} producing hexes`);
    }
    shuffleInPlace(discPool, rng);
    const discAt = new Map<string, number>();
    producing.forEach((c, i) => discAt.set(cellKey(c), discPool[i]!));

    const hexes = sorted.map((c) => ({
      q: c.q,
      r: c.r,
      tile: tileAt.get(cellKey(c))!,
      disc: discAt.get(cellKey(c)) ?? null,
    }));
    if (hasRedAdjacency(hexes, cfg.engine.redNumbers)) continue; // G6: repeat terrain+disc draws

    // harbor kinds: generics + specifics multiset, FY once over the fixed positions
    const kinds = [...Array.from({ length: counts.harborsGeneric }, () => '3:1'), ...counts.harborsSpecific];
    shuffleInPlace(kinds, rng);

    return {
      name: `scaled-${seats}`,
      minSeats: 2,
      maxSeats: seats,
      hexes,
      harbors: positions.map((p, i) => ({ ...p, kind: kinds[i]! })),
      fixedPieces: [],
      overrides: {},
    };
  }
  throw new StateError(`map generation exhausted the rejection bound (${bound}) — layout constraints unsatisfiable`);
}
